import { BaseApiClient } from './base-client'
import {
  type ApiResponse,
  type GraphRecord,
  type GraphSummary,
  API_ROUTES,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'
import type { SaveGraphRequest } from './graphs.client'

/**
 * Admin-only graphs client: addresses any company via ?companyId=
 * (the super-admin company switcher is client-side selection, not a session
 * org change). Mirrors node-agents-admin.client.
 */
export class GraphsAdminApiClient extends BaseApiClient {
  private withCompany(path: string, companyId: string): string {
    return `${path}?companyId=${encodeURIComponent(companyId)}`
  }

  async list(companyId: string): Promise<GraphSummary[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<GraphSummary[]>>(
          this.withCompany(API_ROUTES.ADMIN.GRAPHS.GET_ALL, companyId),
        )
        return response.data ?? []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching graphs (admin)',
      },
    )
    return result ?? []
  }

  async get(companyId: string, id: string): Promise<GraphRecord | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<GraphRecord>>(
          this.withCompany(
            interpolateRoute(API_ROUTES.ADMIN.GRAPHS.GET, { id }),
            companyId,
          ),
        )
        return response.data ?? null
      },
      { on404: 'ignore', errorContext: 'Error fetching graph (admin)' },
    )
  }

  async create(
    companyId: string,
    dto: SaveGraphRequest,
  ): Promise<GraphRecord | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<GraphRecord>>(
          this.withCompany(API_ROUTES.ADMIN.GRAPHS.CREATE, companyId),
          { method: 'POST', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error creating graph (admin)' },
    )
  }

  async save(
    companyId: string,
    id: string,
    dto: SaveGraphRequest,
  ): Promise<GraphRecord | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<GraphRecord>>(
          this.withCompany(
            interpolateRoute(API_ROUTES.ADMIN.GRAPHS.UPDATE, { id }),
            companyId,
          ),
          { method: 'PUT', body: JSON.stringify(dto) },
        )
        return response.data ?? null
      },
      { errorContext: 'Error saving graph (admin)' },
    )
  }

  async remove(companyId: string, id: string): Promise<boolean> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<{ deleted: boolean }>>(
          this.withCompany(
            interpolateRoute(API_ROUTES.ADMIN.GRAPHS.DELETE, { id }),
            companyId,
          ),
          { method: 'DELETE' },
        )
        return response.data?.deleted === true
      },
      { errorContext: 'Error deleting graph (admin)' },
    )
    return result === true
  }
}

export const graphsAdminApiClient = new GraphsAdminApiClient()
